const APPLE_SCRIPT_SRC =
  "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";

export const APPLE_WEB_SIGNIN_EMAIL_KEY = "microlab_apple_web_signin_email";

export function getAppleWebSignInConfig() {
  const clientId = process.env.REACT_APP_APPLE_CLIENT_ID;
  const redirectURI =
    process.env.REACT_APP_APPLE_REDIRECT_URI ||
    `${typeof window !== "undefined" ? window.location.origin : ""}/authentication/sign-in`;
  return { clientId, redirectURI, enabled: Boolean(clientId) };
}

export function loadAppleAuthScript() {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Apple Sign In requires a browser."));
  }
  if (window.AppleID) {
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${APPLE_SCRIPT_SRC}"]`);
    if (existing) {
      const finish = () => {
        if (window.AppleID) resolve();
        else reject(new Error("Apple Sign In failed to initialize."));
      };
      if (window.AppleID) {
        resolve();
        return;
      }
      existing.addEventListener("load", finish);
      existing.addEventListener("error", () => reject(new Error("Apple script failed")));
      return;
    }
    const s = document.createElement("script");
    s.src = APPLE_SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Apple script failed"));
    document.head.appendChild(s);
  });
}

export function decodeAppleJwtPayload(idToken) {
  try {
    const parts = String(idToken).split(".");
    if (parts.length < 2) return {};
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const pad = base64.length % 4;
    const padded = pad ? base64 + "=".repeat(4 - pad) : base64;
    return JSON.parse(atob(padded));
  } catch {
    return {};
  }
}

/**
 * @returns {Promise<{ idToken: string, sub: string, email: string }>}
 */
export async function performAppleWebSignIn() {
  const { clientId, redirectURI, enabled } = getAppleWebSignInConfig();
  if (!enabled) {
    throw new Error("Apple Sign In is not configured for this site.");
  }
  await loadAppleAuthScript();
  if (!window.AppleID?.auth) {
    throw new Error("Apple Sign In is not available in this browser.");
  }
  window.AppleID.auth.init({
    clientId,
    scope: "name email",
    redirectURI,
    usePopup: true,
  });
  const res = await window.AppleID.auth.signIn();
  const idToken = res?.authorization?.id_token;
  if (!idToken) {
    throw new Error("No Apple identity token returned.");
  }
  const payload = decodeAppleJwtPayload(idToken);
  const sub = payload.sub;
  if (!sub) {
    throw new Error("Invalid Apple identity token.");
  }
  let email =
    res?.user?.email ||
    payload.email ||
    (typeof sessionStorage !== "undefined"
      ? sessionStorage.getItem(APPLE_WEB_SIGNIN_EMAIL_KEY)
      : "") ||
    "";
  if (email && typeof sessionStorage !== "undefined") {
    sessionStorage.setItem(APPLE_WEB_SIGNIN_EMAIL_KEY, email);
  }
  return { idToken, sub, email: String(email).trim() };
}
